import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
    getCategoryListingPaginate,
    getSubCategoryListingPaginate,
    getManufacturesListingPaginate,
} from "../../../api/context/manufacturer";
import { getBrandListing } from "../../../api/context/brand";

const FilterModal = ({ show, onHide, onApplyFilters, searchFormik }) => {
    const [activeFilter, setActiveFilter] = useState("Category");

    // Initialize searchQueries with all filter types
    const [searchQueries, setSearchQueries] = useState({
        Category: "",
        Subcategory: "",
        Brands: "",
        Vendors: "",
        Stock: "",
    });

    // Use backend field names directly
    const [localSelections, setLocalSelections] = useState({
        category: [],           // Changed from selectedCategoryList
        subCategory: [],        // Changed from selectedSubCategory
        brands: [],             // Changed from selected_brands
        vendors: [],            // Changed from selected_vendors
        stock: [],
        minPrice: 0,
        maxPrice: 99999,
    });

    const [allCategories, setAllCategories] = useState([]);
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [brandList, setBrandList] = useState([]);
    const [vendorList, setVendorList] = useState([]);
    const [loadingSubCategories, setLoadingSubCategories] = useState(false);

    const stockList = [
        { id: "in_stock", name: "In Stock" },
        { id: "low_stock", name: "Low Stock" },
        { id: "out_of_stock", name: "Out of Stock" },
    ];

    // Helper function to map IDs to full objects
    const mapIdsToObjects = (ids, list) => {
        if (!ids || !Array.isArray(ids)) return [];
        return ids
            .map(id => list.find(item => item && item.id === id))
            .filter(item => item !== undefined);
    };

    useEffect(() => {
        if (show && allCategories.length > 0 && brandList.length > 0 && vendorList.length > 0) {
            // Map formik values (IDs) to local selections (objects) only when modal opens with data
            const categoryIds = searchFormik.values.category || searchFormik.values.selectedCategoryList || [];
            const subCategoryIds = searchFormik.values.subCategory || searchFormik.values.selectedSubCategory || [];
            const brandIds = searchFormik.values.brands || searchFormik.values.selected_brands || [];
            const vendorIds = searchFormik.values.vendors || searchFormik.values.selected_vendors || [];
            const stockIds = searchFormik.values.stock || [];

            setLocalSelections({
                category: mapIdsToObjects(categoryIds, allCategories),
                subCategory: mapIdsToObjects(subCategoryIds, subCategoryList),
                brands: mapIdsToObjects(brandIds, brandList),
                vendors: mapIdsToObjects(vendorIds, vendorList),
                stock: mapIdsToObjects(stockIds, stockList),
                minPrice: searchFormik.values.minPrice || 0,
                maxPrice: searchFormik.values.maxPrice || 99999,
            });
        }
    }, [show]);

    useEffect(() => {
        if (show) fetchAllData();
    }, [show]);

    const fetchAllData = async () => {
        try {
            const categories = await fetchCategories();
            setAllCategories(categories);

            const brands = await fetchBrands();
            setBrandList(brands);

            const vendors = await fetchManufacturers();
            setVendorList(vendors);

            await fetchSubCategoriesBasedOnSelection(localSelections.category);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const fetchSubCategoriesBasedOnSelection = async (selectedCategories) => {
        setLoadingSubCategories(true);
        try {
            let categoryIds = [];

            if (selectedCategories.length === 0) {
                categoryIds = allCategories.map(cat => cat.id);
            } else {
                categoryIds = selectedCategories.map(cat => cat.id);
            }

            if (categoryIds.length === 0) {
                setSubCategoryList([]);
                setLoadingSubCategories(false);
                return;
            }

            getSubCategoryListingPaginate({
                page: 1,
                limit: 500,
                search: '',
                categoryIds: categoryIds,
            })((response) => {
                setLoadingSubCategories(false);
                if (response?.statusCode === 200) {
                    const formatted = response.results.map((item) => ({
                        id: item.id || item.value,
                        name: item.categoryName ? `${item.categoryName} - ${item.title || item.name}` : (item.title || item.name)
                    }));

                    setSubCategoryList(formatted);
                } else {
                    setSubCategoryList([]);
                }
            });
        } catch (err) {
            setLoadingSubCategories(false);
            console.error("Error fetching subcategories", err);
        }
    };

    const fetchCategories = () =>
        new Promise((resolve) => {
            getCategoryListingPaginate({ page: 1, limit: 500, search: "" })((response) => {
                if (response?.statusCode === 200) {
                    resolve(response.results.map(item => ({ id: item.value, name: item.label })));
                } else resolve([]);
            });
        });

    const fetchBrands = () =>
        new Promise((resolve) => {
            getBrandListing({ page: 1, pageSize: 500, search: "" })((response) => {
                if (response?.statusCode === 200) {
                    resolve(response.data.map(item => ({ id: item.id, name: item.name })));
                } else resolve([]);
            });
        });

    const fetchManufacturers = () =>
        new Promise((resolve) => {
            getManufacturesListingPaginate({ page: 1, limit: 500, search: "" })((response) => {
                if (response?.statusCode === 200) {
                    resolve(response.results.map(item => ({ id: item.value, name: item.label })));
                } else resolve([]);
            });
        });

    useEffect(() => {
        if (show && allCategories.length > 0) {
            fetchSubCategoriesBasedOnSelection(localSelections.category);
        }
    }, [localSelections.category, show]);

    const toggleSelection = (key, item) => {
        if (!item || !item.id) return;

        const selected = localSelections[key] || [];
        const exists = selected.some((x) => x && x.id === item.id);

        setLocalSelections(prev => ({
            ...prev,
            [key]: exists
                ? selected.filter((x) => x && x.id !== item.id)
                : [...selected.filter(x => x), item]
        }));
    };

    const handleRowClick = (key, item, e) => {
        if (e.target.type === 'checkbox') {
            return;
        }
        toggleSelection(key, item);
    };

    const filterList = (list, filterKey) => {
        const searchQuery = searchQueries[filterKey] || "";
        if (!searchQuery.trim()) {
            return list;
        }
        
        return list.filter(item => {
            if (!item || !item.name) return false;
            return item.name.toLowerCase().includes(searchQuery.toLowerCase());
        });
    };

    const renderCheckboxList = (list, key) => {
        const filtered = filterList(list, activeFilter); // Use activeFilter here
        const selected = localSelections[key] || [];

        return (
            <>
                <div style={{ overflowY: "auto", flexGrow: 1 }}>
                    {filtered.length > 0 ? (
                        filtered.map((item) => {
                            if (!item || !item.id) return null;

                            const isChecked = selected.some((x) => x && x.id === item.id);

                            return (
                                <div
                                    key={item.id}
                                    onClick={(e) => handleRowClick(key, item, e)}
                                    className="filter-row d-flex"
                                    style={{
                                        cursor: "pointer",
                                        transition: "background 0.15s",
                                        background: isChecked ? "#eef2f7" : "transparent",
                                        borderRadius: 8,
                                        marginBottom: 2,
                                        padding: "12px 10px",
                                        alignItems: "center",
                                        gap: 16,
                                    }}
                                    onMouseEnter={(e) =>
                                        (e.currentTarget.style.background = isChecked ? "#e7edf6" : "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                        (e.currentTarget.style.background = isChecked ? "#eef2f7" : "transparent")
                                    }
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleSelection(key, item)}
                                        style={{
                                            margin: 0,
                                            width: 12,
                                            height: 12,
                                            flex: "0 0 auto",
                                            alignSelf: "center",
                                            WebkitAppearance: "checkbox",
                                            backgroundColor: isChecked ? "#15b8b8ff" : "transparent",
                                        }}
                                    />

                                    <div
                                        style={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: 14,
                                            lineHeight: "18px",
                                            userSelect: "none",
                                        }}
                                    >
                                        {item.name || 'Unnamed Item'}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center p-4">
                            No items found for "{searchQueries[activeFilter]}"
                        </div>
                    )}
                </div>
            </>
        );
    };

    // Map filter display names to state keys
    const filterTypes = [
        { key: "Category", stateKey: "category" },
        { key: "Subcategory", stateKey: "subCategory" },
        { key: "Brands", stateKey: "brands" },
        { key: "Vendors", stateKey: "vendors" },
        { key: "Stock", stateKey: "stock" },
        { key: "Price Range", stateKey: "priceRange" },
    ];

    const getListByKey = (key) => {
        switch (key) {
            case "Category": return allCategories || [];
            case "Subcategory": return subCategoryList || [];
            case "Brands": return brandList || [];
            case "Vendors": return vendorList || [];
            case "Stock": return stockList || [];
            case "Price Range": return []; // Handled separately
            default: return [];
        }
    };

    const getStateKeyForActiveFilter = () => {
        const filter = filterTypes.find(f => f.key === activeFilter);
        return filter ? filter.stateKey : "";
    };

    const handleClearAll = () => {
        setLocalSelections({
            category: [],
            subCategory: [],
            brands: [],
            vendors: [],
            stock: [],
            minPrice: 0,
            maxPrice: 99999,
        });
        // Also clear search queries
        setSearchQueries({
            Category: "",
            Subcategory: "",
            Brands: "",
            Vendors: "",
            Stock: "",
        });
        // Clear formik values as well
        searchFormik.setFieldValue('category', []);
        searchFormik.setFieldValue('subCategory', []);
        searchFormik.setFieldValue('brands', []);
        searchFormik.setFieldValue('vendors', []);
        searchFormik.setFieldValue('stock', []);
        searchFormik.setFieldValue('minPrice', 0);
        searchFormik.setFieldValue('maxPrice', 99999);
    };

    const handleApplyFilters = () => {
        // Convert selections to just IDs for formik
        const formattedValues = {
            category: localSelections.category.map(item => item.id) || [],
            subCategory: localSelections.subCategory.map(item => item.id) || [],
            brands: localSelections.brands.map(item => item.id) || [],
            vendors: localSelections.vendors.map(item => item.id) || [],
            stock: localSelections.stock.map(item => item.id) || [],
            minPrice: localSelections.minPrice || 0,
            maxPrice: localSelections.maxPrice || 99999,
        };

        // Update formik values
        Object.keys(formattedValues).forEach(key => {
            searchFormik.setFieldValue(key, formattedValues[key]);
        });

        // Trigger the filter apply
        onApplyFilters();
        onHide();
    };

    const handleSearchChange = (value) => {
        setSearchQueries(prev => ({
            ...prev,
            [activeFilter]: value
        }));
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="filter-modal-width">
            <Modal.Header closeButton>
                <Modal.Title>Filters</Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0 filter-modal-body">
                <div className="d-flex w-100" style={{ minHeight: "400px" }}>

                    <div className="border-end bg-light" style={{ width: "240px", overflowY: "auto" }}>
                        {filterTypes.map((ft) => (
                            <div
                                key={ft.key}
                                className={`p-3 border-bottom ${activeFilter === ft.key ? "bg-white fw-bold" : ""}`}
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                    setActiveFilter(ft.key);
                                    // Keep the search query when switching filters
                                }}
                            >
                                {ft.key} {localSelections[ft.stateKey]?.length > 0 ? `(${localSelections[ft.stateKey].length})` : ""}
                            </div>
                        ))}
                    </div>

                    <div className="filter-right-panel p-4" style={{ flex: 1 }}>
                        <h6 className="fw-bold mb-3">{activeFilter}</h6>

                        {activeFilter === "Price Range" ? (
                            <div className="price-range-section">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Minimum Price</label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Min Price"
                                        className="mb-3"
                                        style={{ height: "40px" }}
                                        value={localSelections.minPrice}
                                        onChange={(e) => setLocalSelections(prev => ({
                                            ...prev,
                                            minPrice: parseInt(e.target.value) || 0
                                        }))}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Maximum Price</label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Max Price"
                                        className="mb-3"
                                        style={{ height: "40px" }}
                                        value={localSelections.maxPrice}
                                        onChange={(e) => setLocalSelections(prev => ({
                                            ...prev,
                                            maxPrice: parseInt(e.target.value) || 99999
                                        }))}
                                    />
                                </div>
                                <div className="price-range-display mt-3 p-3 bg-light rounded">
                                    <p className="mb-0">
                                        <strong>Price Range: </strong>
                                        ${localSelections.minPrice} - ${localSelections.maxPrice}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <Form.Control
                                    type="text"
                                    placeholder={`Search ${activeFilter.toLowerCase()}...`}
                                    className="mb-4"
                                    style={{ height: "40px" }}
                                    value={searchQueries[activeFilter] || ""}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                />

                                <div className="filter-scroll-area" style={{ height: "300px", overflowY: "auto" }}>
                                    {renderCheckboxList(
                                        getListByKey(activeFilter), 
                                        getStateKeyForActiveFilter()
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
               
                {/* <Button className="cancel_btn" onClick={onHide}>
                    Cancel
                </Button> */}
                 <Button variant="link" className="text-dark" onClick={handleClearAll}>
                    Clear all
                </Button>
                <Button className="request_btn" onClick={handleApplyFilters}>
                    Apply filters
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FilterModal;